import { useState } from 'react';
import {
    X,
    Copy,
    Check,
    QrCode,
    MessageCircle,
    Link as LinkIcon,
    Share2,
    Smartphone
} from 'lucide-react';

interface InviteQRModalProps {
    isOpen: boolean;
    onClose: () => void;
    inviteCode: string;
    athleteName?: string;
}

export function InviteQRModal({ isOpen, onClose, inviteCode, athleteName }: InviteQRModalProps) {
    const [copied, setCopied] = useState(false);

    if (!isOpen) return null;

    const inviteUrl = `${window.location.origin}/convite/${inviteCode}`;
    const qrCodeUrl = `https://api.qrserver.com/v1/create-qr-code/?size=300x300&data=${encodeURIComponent(inviteUrl)}&bgcolor=ffffff&color=6366f1`;

    const whatsappMessage = encodeURIComponent(
        `💪 *Convite GoFit*\n\n` +
        `Olá${athleteName ? ` ${athleteName}` : ''}!\n\n` +
        `Você foi convidado para fazer parte do meu time de atletas! ` +
        `Clique no link abaixo para aceitar o convite e começar sua jornada fitness:\n\n` +
        `👉 ${inviteUrl}\n\n` +
        `Nos vemos nos treinos! 💪`
    );

    const handleCopy = () => {
        navigator.clipboard.writeText(inviteUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleWhatsApp = () => {
        window.open(`https://wa.me/?text=${whatsappMessage}`, '_blank');
    };

    const handleShare = async () => {
        if (navigator.share) {
            try {
                await navigator.share({
                    title: 'Convite GoFit',
                    text: `Você foi convidado para fazer parte do time de atletas!`,
                    url: inviteUrl,
                });
            } catch (err) {
                // User cancelled or error
            }
        } else {
            handleCopy();
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
            <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md overflow-hidden animate-scale-in">
                {/* Header */}
                <div className="p-4 border-b border-gray-200 dark:border-gray-800 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
                        <QrCode className="w-5 h-5 text-primary-600" />
                        Convite para Atleta
                    </h3>
                    <button
                        onClick={onClose}
                        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
                    >
                        <X className="w-5 h-5 text-gray-500" />
                    </button>
                </div>

                {/* QR Code */}
                <div className="p-6 flex flex-col items-center">
                    <div className="p-4 bg-white rounded-2xl shadow-lg border-4 border-primary-100 dark:border-primary-900">
                        <img
                            src={qrCodeUrl}
                            alt="QR Code do Convite"
                            className="w-48 h-48"
                        />
                    </div>
                    <p className="mt-4 text-sm text-gray-500 text-center">
                        Peça ao atleta para apontar a câmera do celular para o QR Code
                    </p>
                </div>

                {/* Divider */}
                <div className="px-6">
                    <div className="flex items-center gap-3">
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                        <span className="text-xs text-gray-400 uppercase">ou envie o link</span>
                        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700" />
                    </div>
                </div>

                {/* Link Display */}
                <div className="p-6 space-y-4">
                    <div className="flex items-center gap-2 p-3 bg-gray-100 dark:bg-gray-800 rounded-xl">
                        <LinkIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
                        <span className="flex-1 text-sm text-gray-600 dark:text-gray-400 truncate">
                            {inviteUrl}
                        </span>
                        <button
                            onClick={handleCopy}
                            className="p-2 rounded-lg bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 transition-colors"
                        >
                            {copied ? (
                                <Check className="w-4 h-4 text-green-500" />
                            ) : (
                                <Copy className="w-4 h-4 text-gray-500" />
                            )}
                        </button>
                    </div>

                    {/* Action Buttons */}
                    <div className="grid grid-cols-2 gap-3">
                        <button
                            onClick={handleWhatsApp}
                            className="flex items-center justify-center gap-2 py-3 px-4 bg-green-500 hover:bg-green-600 text-white rounded-xl font-medium transition-colors"
                        >
                            <MessageCircle className="w-5 h-5" />
                            WhatsApp
                        </button>
                        <button
                            onClick={handleShare}
                            className="flex items-center justify-center gap-2 py-3 px-4 bg-primary-600 hover:bg-primary-700 text-white rounded-xl font-medium transition-colors"
                        >
                            <Share2 className="w-5 h-5" />
                            Compartilhar
                        </button>
                    </div>
                </div>

                {/* Instructions */}
                <div className="px-6 pb-6">
                    <div className="p-4 bg-blue-50 dark:bg-blue-900/20 rounded-xl border border-blue-200 dark:border-blue-800">
                        <div className="flex items-start gap-3">
                            <Smartphone className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                            <div className="text-sm text-blue-700 dark:text-blue-300">
                                <p className="font-medium mb-1">Como funciona:</p>
                                <ol className="list-decimal list-inside space-y-1 text-blue-600 dark:text-blue-400">
                                    <li>Atleta escaneia o QR ou clica no link</li>
                                    <li>Se já tem conta, apenas vincula</li>
                                    <li>Se não tem, cria conta e vincula automaticamente</li>
                                </ol>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
